import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import QuizIcon from "@mui/icons-material/Quiz";
import {
	Box,
	Card,
	CircularProgress,
	Container,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI_TIMEZONE } from "@/configConstants";
import { NotificationSnackbars } from "@/frontend/components/NotificationSnackbars";
import { formatDeDateRange } from "@/frontend/utils";
import type { AttendanceStatus, EventRecord } from "@/types/event-attendance";
import { parseEventDate } from "../../utils/dates";
import type { AttendanceInfoItem } from "./types";

export const WelcomeText: React.FC = () => (
	<Card variant="elevation" sx={{ bgcolor: "rgba(255,255,255,0.7)" }}>
		<Box sx={{ p: 2, textAlign: "center" }}>
			<Typography variant="h5" gutterBottom>
				Es geht wieder los!
			</Typography>
			<Typography variant="body1">
				Es gibt neue Termine für die kleine Eisfläche.
				<br />
				Bitte schnell eintragen, damit ich buchen kann.
			</Typography>
		</Box>
	</Card>
);

export const AttendanceIcons: React.FC<{
	yes: number;
	no: number;
	status?: string;
}> = React.memo(({ yes, no, status }) => (
	<Stack direction="row" spacing={2} alignItems="center">
		{status === "scheduled" && (
			<Box sx={{ display: { xs: "none", md: "block" } }}>
				<Tooltip title="Dieser Termin ist noch nicht bei der Arena fest gebucht. Bitte eintragen damit ich buchen kann.">
					<QuizIcon fontSize="small" sx={{ fontSize: "0.8em" }} />
				</Tooltip>
			</Box>
		)}
		<Stack direction="row" spacing={0.5} alignItems="center">
			<Tooltip title="Zugesagt">
				<CheckIcon color="info" fontSize="small" sx={{ fontSize: "1em" }} />
			</Tooltip>
			<Typography variant="body2">{yes}</Typography>
		</Stack>
		<Stack
			direction="row"
			spacing={0.5}
			alignItems="center"
			sx={{ display: { xs: "none", md: "flex" } }}
		>
			<Tooltip title="Abgesagt">
				<CloseIcon color="error" fontSize="small" sx={{ fontSize: "1em" }} />
			</Tooltip>
			<Typography variant="body2">{no}</Typography>
		</Stack>
	</Stack>
));

export interface AttendanceToggleProps {
	value: AttendanceStatus | undefined;
	disabled?: boolean;
	onChange: (next: AttendanceStatus | null) => void;
	testId?: string;
}

export const AttendanceToggle: React.FC<AttendanceToggleProps> = ({
	value,
	onChange,
	disabled,
	testId,
}) => (
	<ToggleButtonGroup
		exclusive
		size="small"
		value={value}
		onChange={(_, newValue) => {
			if (newValue === null) return;
			onChange(newValue as AttendanceStatus);
		}}
		disabled={disabled}
		aria-label="Teilnahme festlegen"
		data-testid={testId}
	>
		<ToggleButton
			color="info"
			value="yes"
			sx={{
				"&.Mui-selected": {
					bgcolor: (theme) => alpha(theme.palette.info.main, 0.8),
					color: (theme) => theme.palette.info.contrastText,
					"&:hover": {
						bgcolor: (theme) => alpha(theme.palette.info.main, 0.95),
					},
				},
			}}
			aria-label="Zusage"
			data-testid={testId ? `${testId}-yes` : undefined}
		>
			<CheckIcon sx={{ fontSize: "1.5em" }} />
		</ToggleButton>
		<ToggleButton
			color="error"
			value="no"
			sx={{
				"&.Mui-selected": {
					bgcolor: (theme) => alpha(theme.palette.error.main, 0.8),
					color: (theme) => theme.palette.error.contrastText,
					"&:hover": {
						bgcolor: (theme) => alpha(theme.palette.error.main, 0.95),
					},
				},
			}}
			aria-label="Absage"
			data-testid={testId ? `${testId}-no` : undefined}
		>
			<CloseIcon sx={{ fontSize: "1.5em" }} />
		</ToggleButton>
	</ToggleButtonGroup>
);

export interface DetailListProps {
	title: string;
	items: { userId: string; name: string }[];
	iconColor: "primary" | "warning";
	emptyLabel: string;
}

export const DetailList: React.FC<DetailListProps> = ({
	title,
	items,
	iconColor,
	emptyLabel,
}) => (
	<Box sx={{ flex: 1 }}>
		<Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
			{title} ({items.length})
		</Typography>
		<List dense>
			{items.map((u) => (
				<ListItem key={u.userId} sx={{ py: 0.25 }}>
					<ListItemIcon sx={{ minWidth: 32 }}>
						<PersonIcon fontSize="small" color={iconColor} />
					</ListItemIcon>
					<ListItemText primary={u.name} />
				</ListItem>
			))}
			{items.length === 0 && (
				<ListItem>
					<ListItemText primary={emptyLabel} />
				</ListItem>
			)}
		</List>
	</Box>
);

export const EventsContainer: React.FC<{
	children: React.ReactNode;
	saving: boolean;
	errorMsg: string | null;
	successMsg: string | null;
	onCloseError: () => void;
	onCloseSuccess: () => void;
}> = ({
	children,
	saving,
	errorMsg,
	successMsg,
	onCloseError,
	onCloseSuccess,
}) => (
	<Container
		maxWidth="md"
		disableGutters
		sx={{
			position: "relative",
			flex: 1,
			width: "100%",
			mx: "auto",
			my: 0,
			px: { xs: 2, sm: 3 },
			py: { xs: 2, sm: 3 },
			WebkitOverflowScrolling: "touch",
			bgcolor: "rgba(255,255,255,0.35)",
			boxShadow: 6,
			backdropFilter: "blur(4px)",
		}}
	>
		{saving && (
			<Box sx={{ position: "sticky", top: 0, zIndex: 1 }}>
				<LinearProgress />
			</Box>
		)}
		{children}
		<NotificationSnackbars
			errorMsg={errorMsg}
			successMsg={successMsg}
			onCloseError={onCloseError}
			onCloseSuccess={onCloseSuccess}
		/>
	</Container>
);

export const LoadingRow: React.FC = () => (
	<Stack direction="row" alignItems="center" spacing={1}>
		<CircularProgress size={20} />
		<Typography>Wird geladen…</Typography>
	</Stack>
);

export function ClickableCell(props: {
	isManager: boolean;
	onClick: () => void;
	children: React.ReactNode;
	opacity?: number;
}) {
	const { isManager, onClick, children, opacity } = props;
	return (
		<Box
			sx={{
				opacity,
				cursor: isManager ? "pointer" : "default",
				"&:hover": isManager
					? {
							textDecoration: "underline",
							color: "primary.main",
						}
					: {},
			}}
			onClick={() => isManager && onClick()}
		>
			{children}
		</Box>
	);
}

export function ExpandedAttendancePanel(props: { info: AttendanceInfoItem }) {
	const { info } = props;
	return (
		<Box
			sx={{
				gridColumn: "1 / -1",
				border: "1px solid #e0e0e0",
				borderRadius: 1,
				p: { xs: 1, sm: 2 },
				ml: { sm: 1, md: 12 },
				mt: -0.5,
				mb: 0.5,
				bgcolor: "rgba(255, 255, 255, 0.25)",
			}}
		>
			<Stack
				direction={{ sm: "column", md: "row" }}
				spacing={4}
				alignItems={{ xs: "stretch", sm: "flex-start" }}
			>
				<DetailList
					title="Zugesagt"
					items={info.yes}
					iconColor="primary"
					emptyLabel="Keine Zusagen"
				/>
				<DetailList
					title="Abgesagt"
					items={info.no}
					iconColor="warning"
					emptyLabel="Keine Absagen"
				/>
			</Stack>
		</Box>
	);
}

export function EventRowFragment(props: {
	ev: EventRecord;
	info: AttendanceInfoItem;
	loggedIn: boolean;
	isManager: boolean;
	pending: boolean;
	isExpanded: boolean;
	onToggle: (eventId: number, next: AttendanceStatus | null) => void;
	onExpand: (eventId: number) => void;
	onNavigate: (eventId: number) => void;
	readonly?: boolean;
	dimmed?: boolean;
}) {
	const {
		ev,
		info,
		loggedIn,
		isManager,
		pending,
		isExpanded,
		onToggle,
		onExpand,
		onNavigate,
		readonly,
		dimmed,
	} = props;
	const { weekday, date, timeRange } = formatDeDateRange(
		ev.date,
		ev.start_time,
		ev.end_time,
	);
	const opacity = dimmed ? 0.8 : undefined;

	return (
		<React.Fragment>
			<Box
				sx={{
					display: "flex",
					justifyContent: "left",
					opacity: readonly ? 0.6 : 1,
				}}
			>
				{pending ? (
					<CircularProgress size={15} />
				) : (
					<AttendanceToggle
						value={info.my}
						onChange={(next) => onToggle(ev.id, next)}
						disabled={!!readonly}
						testId={`attendance-toggle-${ev.id}`}
					/>
				)}
			</Box>

			<ClickableCell
				isManager={isManager}
				onClick={() => onNavigate(ev.id)}
				opacity={opacity}
			>
				<Typography
					variant="body2"
					sx={{ display: { xs: "none", sm: "block" } }}
				>
					{weekday}
				</Typography>
				<Typography
					variant="body2"
					sx={{ display: { xs: "block", sm: "none" } }}
				>
					{weekday.slice(0, 2)}
				</Typography>
			</ClickableCell>

			<ClickableCell
				isManager={isManager}
				onClick={() => onNavigate(ev.id)}
				opacity={opacity}
			>
				<Typography variant="body2">{date}</Typography>
			</ClickableCell>

			<ClickableCell
				isManager={isManager}
				onClick={() => onNavigate(ev.id)}
				opacity={opacity}
			>
				<Typography variant="body2">{timeRange.replace(" - ", "-")}</Typography>
			</ClickableCell>

			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					alignItems: "center",
					opacity,
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<AttendanceIcons
						yes={info.yesCount}
						no={info.noCount}
						status={ev.status}
					/>
					{loggedIn && (
						<IconButton size="small" onClick={() => onExpand(ev.id)}>
							<ExpandMoreIcon
								fontSize="small"
								sx={{
									transform: isExpanded ? "rotate(180deg)" : "none",
									transition: "0.2s",
								}}
							/>
						</IconButton>
					)}
				</Stack>
			</Box>

			{loggedIn && isExpanded && <ExpandedAttendancePanel info={info} />}
		</React.Fragment>
	);
}

export function MonthSection(props: {
	monthKey: string;
	events: EventRecord[];
	attendanceInfo: Record<number, AttendanceInfoItem>;
	pendingById: Record<number, boolean>;
	loggedIn: boolean;
	isManager: boolean;
	onToggle: (eventId: number, next: AttendanceStatus | null) => void;
}) {
	const {
		monthKey,
		events,
		attendanceInfo,
		pendingById,
		loggedIn,
		isManager,
		onToggle,
	} = props;

	const [expanded, setExpanded] = useState<Record<number, boolean>>({});
	const navigate = useNavigate();

	const handleExpandToggle = useCallback((eventId: number) => {
		setExpanded((ex) => ({ ...ex, [eventId]: !ex[eventId] }));
	}, []);

	const handleNavigateToParticipation = useCallback(
		(eventId: number) => {
			navigate(`/participation/${eventId}`);
		},
		[navigate],
	);

	const monthDate = parseEventDate(`${monthKey}-01`).toJSDate();
	const monthName = new Intl.DateTimeFormat("de-DE", {
		month: "long",
		timeZone: UI_TIMEZONE,
	}).format(monthDate);

	return (
		<Card
			key={monthKey}
			variant="elevation"
			sx={{ bgcolor: "rgba(255,255,255,0.7)" }}
		>
			<Typography
				variant="subtitle1"
				component="div"
				sx={{ p: 2, pt: 1, pb: 0 }}
			>
				{monthName}
			</Typography>

			<Box sx={{ p: 2 }}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "80px 1fr 1fr 2fr 1fr",
							sm: "110px 1fr 4fr 16fr 1fr",
						},
						alignItems: "center",
						rowGap: 1,
						columnGap: 0.5,
					}}
				>
					{events.map((ev) => {
						const info = attendanceInfo[ev.id]!;
						const isExpanded = !!expanded[ev.id];
						return (
							<EventRowFragment
								key={ev.id}
								ev={ev}
								info={info}
								loggedIn={loggedIn}
								isManager={isManager}
								pending={!!pendingById[ev.id]}
								isExpanded={isExpanded}
								onToggle={onToggle}
								onExpand={handleExpandToggle}
								onNavigate={handleNavigateToParticipation}
							/>
						);
					})}
				</Box>
			</Box>
		</Card>
	);
}

export function PastEventsSection(props: {
	pastEvents: EventRecord[];
	pastExpanded: boolean;
	setPastExpanded: (v: boolean) => void;
	attendanceInfo: Record<number, AttendanceInfoItem>;
	loggedIn: boolean;
	isManager: boolean;
}) {
	const {
		pastEvents,
		pastExpanded,
		setPastExpanded,
		attendanceInfo,
		loggedIn,
		isManager,
	} = props;

	const [expanded, setExpanded] = useState<Record<number, boolean>>({});
	const navigate = useNavigate();

	const handleExpandToggle = useCallback((eventId: number) => {
		setExpanded((ex) => ({ ...ex, [eventId]: !ex[eventId] }));
	}, []);

	const handleNavigateToParticipation = useCallback(
		(eventId: number) => {
			navigate(`/participation/${eventId}`);
		},
		[navigate],
	);

	if (!loggedIn || pastEvents.length === 0) return null;

	return (
		<Card variant="elevation" sx={{ bgcolor: "rgba(255,255,255,0.7)" }}>
			<Box
				sx={{
					p: 2,
					pt: 1,
					pb: pastExpanded ? 0 : 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					"&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
				}}
				onClick={() => setPastExpanded(!pastExpanded)}
			>
				<Typography variant="subtitle1" component="div">
					Vergangene Termine ({pastEvents.length})
				</Typography>
				<IconButton size="small">
					<ExpandMoreIcon
						fontSize="small"
						sx={{
							transform: pastExpanded ? "rotate(180deg)" : "none",
							transition: "0.2s",
						}}
					/>
				</IconButton>
			</Box>

			{pastExpanded && (
				<Box sx={{ p: 2, pt: 0 }}>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "80px 1fr 1fr 2fr 1fr",
								sm: "110px 1fr 4fr 16fr 1fr",
							},
							alignItems: "center",
							rowGap: 1,
							columnGap: 0.5,
						}}
					>
						{pastEvents.map((ev) => {
							const info = attendanceInfo[ev.id]!;
							const isExpanded = !!expanded[ev.id];
							return (
								<EventRowFragment
									key={ev.id}
									ev={ev}
									info={info}
									loggedIn={loggedIn}
									isManager={isManager}
									pending={false}
									isExpanded={isExpanded}
									onToggle={() => {}}
									onExpand={handleExpandToggle}
									onNavigate={handleNavigateToParticipation}
									readonly
									dimmed
								/>
							);
						})}
					</Box>
				</Box>
			)}
		</Card>
	);
}
